import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import { Typography } from "components/atoms";
import { NavLeftArrow, NavRightArrow } from "components/atoms/Icon";

interface IProps {
  popUpIncrementMonth?: (month: number) => void;
  popUpIncrementYears?: (year: number) => void;
  isMonthPopUpShow?: boolean;
  isYearPopUpShow?: boolean;
  maxDate?: any;
  minDate?: any;
  studyPlannerCurrentMonth?: any;
  setMonthPopUpShow?: () => void;
  theme?: "light" | "dark";
}

type MonthMapper = {
  [key: number]: string;
};

const monthVariantsMapping: MonthMapper = {
  0: "Jan",
  1: "Feb",
  2: "Mar",
  3: "Apr",
  4: "May",
  5: "Jun",
  6: "Jul",
  7: "Aug",
  8: "Sep",
  9: "Oct",
  10: "Nov",
  11: "Dec",
};

export const MonthNavigatorPopUp = ({
  popUpIncrementMonth,
  popUpIncrementYears,
  isMonthPopUpShow,
  isYearPopUpShow,
  maxDate,
  minDate,
  studyPlannerCurrentMonth,
  setMonthPopUpShow,
  theme
}: IProps) => {
  const [years, setYears] = useState<any[]>([]);
  const currentYear = dayjs(studyPlannerCurrentMonth).year();
  const currentMonth = dayjs(studyPlannerCurrentMonth).month();
  const [year, setYear] = useState(currentYear);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const months = [
    [0, 1, 2],
    [3, 4, 5],
    [6, 7, 8],
    [9, 10, 11],
  ];
  const minYear = dayjs(minDate).year();
  const maxYear = dayjs(maxDate).year();
  const increment = () => {
    if (!isYearPopUpShow && maxYear > year) {
      if (year < currentYear + 5) {
        setYear(prev => prev + 1);
        popUpIncrementYears && popUpIncrementYears(year + 1);
        setMonthPopUpShow && setMonthPopUpShow();
      }
    }
  };

  const decrement = () => {
    if (!isYearPopUpShow && minYear < year) {
      if (year > currentYear - 6) {
        setYear(prev => prev - 1);
        popUpIncrementYears && popUpIncrementYears(year - 1);
        setMonthPopUpShow && setMonthPopUpShow();
      }
    }
  };

  const setPlannerMonth = (month: number) => {
    setSelectedMonth(month);
    popUpIncrementMonth && popUpIncrementMonth(month);
  };

  const setPlannerYear = (year: number) => {
    setYear(year);
    popUpIncrementYears && popUpIncrementYears(year);
    setMonthPopUpShow && setMonthPopUpShow();
  };

  useEffect(() => {
    setYears([]);
    const yearOffsets = [
      [-6, -5, -4],
      [-3, -2, -1],
      [0, 1, 2],
      [3, 4, 5],
    ];

    yearOffsets.forEach(offsets => {
      const tempArry = offsets.map(offset => currentYear + offset);
      setYears(prevYears => [...prevYears, tempArry]);
    });
  }, []);

  return (
    <div className='month-navigator-popup-wrapper'>
      {years.length > 0 && (
        <div className='current-year-holder'>
          {isMonthPopUpShow && <Typography variant='h4Med' label={`${year}`} />}
          {isYearPopUpShow && <Typography variant='h4Med' label={`${currentYear - 6} - ${currentYear + 5}`} />}
          <NavLeftArrow
            theme={theme}
            onClick={decrement}
            style={isYearPopUpShow ? "disable-nav-arrow" : year <= currentYear - 6 || year <= minYear ? "disable-nav-arrow" : "nav-arrow"}
            isDisable={year <= currentYear - 6 || isYearPopUpShow || year <= minYear}
          />

          <NavRightArrow
            theme={theme}
            style={isYearPopUpShow ? "disable-nav-arrow" : year >= currentYear + 5 || year >= maxYear ? "disable-nav-arrow" : "nav-arrow"}
            isDisable={year >= currentYear + 5 || isYearPopUpShow || year >= maxYear}
            onClick={increment}
          />
        </div>
      )}
      {isMonthPopUpShow && (
        <div className='month-holder'>
          {years.length > 0 &&
            months.map((item: any, index: number) => (
              <div
                className={classNames("month-holder-row", {
                  "border-bottom": months.length - 1 === index,
                })}>
                {item.map((month: number, index: number) => {
                  const isInvalidMonth =
                    dayjs(minDate).year() > year ||
                    (dayjs(minDate).year() === year && dayjs(minDate).month() > month) ||
                    dayjs(maxDate).year() < year ||
                    (dayjs(maxDate).year() === year && dayjs(maxDate).month() < month);
                  return (
                    <div
                      className='item-wrapper'
                      onClick={() => {
                        if (!isInvalidMonth) {
                          setPlannerMonth(month);
                        }
                      }}>
                      <Typography
                        label={monthVariantsMapping[month]}
                        variant='h8Med'
                        styles={
                          dayjs(minDate).year() > year || dayjs(maxDate).year() < year
                            ? "invalid-date"
                            : isInvalidMonth
                            ? "invalid-date"
                            : currentMonth === month
                            ? "current-item-bg-custom-style"
                            : "other-item-bg-custom-style"
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      )}
      {isYearPopUpShow && (
        <div className='year-holder'>
          {years.length > 0 &&
            years.map((item: any, index: number) => (
              <div
                className={classNames("year-holder-row", {
                  "border-bottom": years.length - 1 === index,
                })}>
                {item.map((year: number, index: number) => {
                  const isInvalidYear = dayjs(minDate).year() > year || dayjs(maxDate).year() < year;
                  return (
                    <div
                      className={classNames("item-wrapper")}
                      onClick={() => {
                        if (!isInvalidYear) {
                          setPlannerYear(year);
                        }
                      }}>
                      <Typography
                        label={`${year}`}
                        variant='h8Med'
                        styles={
                          currentYear === year
                            ? "current-item-bg-custom-style"
                            : dayjs(minDate).year() > year || dayjs(maxDate).year() < year
                            ? "invalid-date"
                            : "other-item-bg-custom-style"
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
